<template>
  <div class="video-carousel-section">
    <!-- Title -->
    <h1 class="section-title">
      <span>{{ t('video_carousel.intro_part1') }}</span>
      <span class="highlight">{{ t('video_carousel.intro_highlight') }}</span>
      <span>{{ t('video_carousel.intro_part2') }}</span>
    </h1>

    <!-- Carousel Container -->
    <div class="carousel-container">
      <!-- Left Navigation Button -->
      <button
        class="nav-button nav-left"
        @click="moveLeft"
        :aria-label="t('video_carousel.prev_aria')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
        </svg>
      </button>

      <!-- Carousel Track -->
      <div class="carousel-track">
        <!-- Left Side Card -->
        <div
          v-if="items[leftIndex]"
          class="video-card side-card left-card"
          @click="openPopup(items[leftIndex].videoUrl)"
        >
          <img
            :src="items[leftIndex].thumbnailUrl"
            :alt="items[leftIndex].alt || t('video_carousel.default_alt')"
            class="video-thumbnail"
            loading="lazy"
          />
        </div>

        <!-- Center Card (Main) -->
        <div v-if="items[centerIndex]" class="video-card center-card">
          <div class="card-content">
            <!-- Video Thumbnail -->
            <img
              :src="items[centerIndex].thumbnailUrl"
              :alt="items[centerIndex].alt || t('video_carousel.default_alt')"
              class="video-thumbnail"
              @click="openPopup(items[centerIndex].videoUrl)"
              loading="lazy"
            />

            <!-- Play Button -->
            <button
              class="play-button"
              @click="openPopup(items[centerIndex].videoUrl)"
              aria-label="Play video"
            >
              <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                <circle cx="50" cy="50" r="45" fill="rgba(255, 255, 255, 0.9)" />
                <polygon points="40,30 70,50 40,70" fill="#FF6B35" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Right Side Card -->
        <div
          v-if="items[rightIndex]"
          class="video-card side-card right-card"
          @click="openPopup(items[rightIndex].videoUrl)"
        >
          <img
            :src="items[rightIndex].thumbnailUrl"
            :alt="items[rightIndex].alt || t('video_carousel.default_alt')"
            class="video-thumbnail"
            loading="lazy"
          />
        </div>
      </div>

      <!-- Right Navigation Button -->
      <button
        class="nav-button nav-right"
        @click="moveRight"
        :aria-label="t('video_carousel.next_aria')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
        </svg>
      </button>
    </div>

    <!-- CTA Button -->
    <div class="cta-container">
      <CtaButton :url="ctaUrl" @click="handleCTA" />
    </div>

    <!-- Video Popup Modal -->
    <Transition name="fade">
      <div v-if="isPopupOpen" class="popup-overlay" @click="closePopup">
        <div class="popup-content" @click.stop>
          <button class="close-button" @click="closePopup" aria-label="Close video">
            &times;
          </button>
          <iframe
            v-if="currentVideoSrc"
            :src="currentVideoSrc"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            class="popup-video"
          ></iframe>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useVideoPopup } from '@/composables/useVideoPopup'
import { useLanguage } from '@/composables/useLanguage'
import CtaButton from '@/components/ui/CtaButton.vue'

const { t } = useLanguage()

const props = defineProps({
  items: {
    type: Array,
    required: true,
    validator: (value) => {
      return value.every(item =>
        item.thumbnailUrl &&
        item.videoUrl
      )
    }
  },
  ctaUrl: {
    type: String,
    default: '#'
  }
})

const { isPopupOpen, currentVideoSrc, openPopup, closePopup } = useVideoPopup()

const currentPosition = ref(0)

const centerIndex = computed(() => {
  return (currentPosition.value % props.items.length + props.items.length) % props.items.length
})

const leftIndex = computed(() => {
  return ((currentPosition.value - 1) % props.items.length + props.items.length) % props.items.length
})

const rightIndex = computed(() => {
  return (currentPosition.value + 1) % props.items.length
})

const moveLeft = () => {
  currentPosition.value -= 1
}

const moveRight = () => {
  currentPosition.value += 1
}

const handleCTA = () => {
  // Additional logic if needed when CTA is clicked
  console.log('CTA clicked')
}
</script>

<style scoped>
/* Section Container */
.video-carousel-section {
  width: 100%;
  max-width: 100vw;
  position: relative;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(30px, 6vw, 40px) clamp(20px, 4vw, 40px);
  box-sizing: border-box;
  gap: clamp(15px, 3vw, 20px);
  overflow: hidden;
}

/* RTL specific - Reduced padding and gap */
html[lang="ar"] .video-carousel-section {
  padding: 20px 20px;
  gap: 12px;
}

/* Section Title */
.section-title {
  max-width: 1400px;
  width: 100%;
  font-size: clamp(24px, 4.5vw, 40px);
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  text-transform: uppercase;
  color: #313131;
  margin: 0 0 clamp(15px, 3vw, 20px);
  font-family: Poppins, sans-serif;
  padding: 0 clamp(10px, 2vw, 20px);
  box-sizing: border-box;
}

/* RTL specific - Reduced margin */
html[lang="ar"] .section-title {
  margin: 0 0 10px;
  line-height: 1.15;
}

.section-title .highlight {
  color: #f47d21;
}

/* Carousel Container */
.carousel-container {
  width: 100%;
  max-width: 1600px;
  position: relative;
  height: clamp(600px, 100vw, 920px);
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1500px;
  box-sizing: border-box;
}

/* Carousel Track */
.carousel-track {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0px;
  transform-style: preserve-3d;
}

/* Video Cards */
.video-card {
  position: relative;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

/* Center Card (Main) */
.center-card {
  z-index: 10;
  transform: scale(1);
}

.center-card .card-content {
  position: relative;
  width: 436px;
  height: 749px;
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 40px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.center-card .video-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 40px;
  display: block;
}

.center-card:hover .video-thumbnail {
  transform: scale(1.02);
}

/* Play Button */
.play-button {
  position: absolute;
  top: 35%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90px;
  height: 90px;
  background: none;
  border: none;
  cursor: pointer;
  transition: transform 0.3s ease;
  z-index: 5;
  padding: 0;
}

.play-button:hover {
  transform: translate(-50%, -50%) scale(1.15);
}

.play-button svg {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.4));
}


/* Side Cards */
.side-card {
  position: absolute;
  width: 373px;
  height: 641px;
  border-radius: 40px;
  overflow: hidden;
  opacity: 0.8;
  z-index: 5;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
}

.side-card .video-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 40px;
}

.left-card {
  top: 50%;
  left: calc(50% - 650px);
  transform: translateY(-50%);
}

.right-card {
  top: 50%;
  right: calc(50% - 650px);
  transform: translateY(-50%);
}

/* Navigation Buttons */
.nav-button {
  position: absolute;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: #0B5A7D;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 20;
  box-shadow: 0 4px 12px rgba(11, 90, 125, 0.3);
}

.nav-button:hover {
  background: #094a68;
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(11, 90, 125, 0.5);
}

.nav-button:active {
  transform: scale(0.95);
}

.nav-button svg {
  width: 26px;
  height: 26px;
}

.nav-left {
  left: 0;
}

.nav-right {
  right: 0;
}

/* CTA Container */
.cta-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: clamp(15px, 3vw, 20px);
  width: 100%;
  max-width: 800px;
  padding: 0 clamp(10px, 2vw, 20px);
  box-sizing: border-box;
}

/* RTL specific - Reduced margin */
html[lang="ar"] .cta-container {
  margin-top: 10px;
}

/* Popup Modal */
.popup-overlay {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(8px);
}

.popup-content {
  width: 28vw;
  max-width: 500px;
  height: 95vh;
  overflow: hidden;
  position: relative;
  border-radius: 20px;
  box-shadow: 0 25px 100px rgba(0, 0, 0, 0.7);
  background: #000;
}

.popup-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 20px;
}

.close-button {
  position: absolute;
  top: 15px;
  left: 20px;
  font-size: 3rem;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #fff;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  z-index: 10;
}

.close-button:hover {
  background: #d9534f;
  transform: rotate(90deg);
}

/* Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 1400px) {
  .carousel-container {
    height: 800px;
  }

  .center-card .card-content {
    width: 380px;
    height: 655px;
  }

  .center-card .video-thumbnail {
    height: 100%;
  }

  .side-card {
    width: 320px;
    height: 550px;
  }

  .left-card {
    left: 30px;
  }

  .right-card {
    right: 30px;
  }
}

@media (max-width: 1024px) {
  .section-title {
    font-size: 32px;
    padding: 0 20px;
  }

  .carousel-container {
    height: 700px;
  }

  .center-card .card-content {
    width: 340px;
    height: 570px;
  }

  .center-card .video-thumbnail {
    height: 100%;
  }

  .side-card {
    width: 280px;
    height: 480px;
  }

  .cta-button {
    font-size: 18px;
    padding: 16px 48px;
  }
}

@media (max-width: 768px) {
  .video-carousel-section {
    padding: clamp(35px, 6vw, 50px) clamp(15px, 3vw, 20px);
    gap: clamp(20px, 4vw, 30px);
  }

  .carousel-container {
    height: clamp(550px, 90vw, 600px);
  }

  .center-card .card-content {
    width: clamp(280px, 70vw, 300px);
    height: clamp(460px, 115vw, 490px);
  }

  .center-card .video-thumbnail {
    height: 100%;
  }

  .side-card {
    display: none;
  }

  .nav-button {
    width: clamp(40px, 9vw, 44px);
    height: clamp(40px, 9vw, 44px);
  }

  .nav-button svg {
    width: clamp(20px, 4.5vw, 22px);
    height: clamp(20px, 4.5vw, 22px);
  }

  .nav-left {
    left: clamp(8px, 2vw, 10px);
  }

  .nav-right {
    right: clamp(8px, 2vw, 10px);
  }

  .play-button {
    width: clamp(65px, 14vw, 70px);
    height: clamp(65px, 14vw, 70px);
  }

  .popup-content {
    width: 90%;
    max-width: 400px;
    height: auto;
    aspect-ratio: 9/16;
  }

  .popup-video {
    height: auto;
    aspect-ratio: 9/16;
  }
}

@media (max-width: 640px) {
  .video-carousel-section {
    padding: clamp(30px, 5vw, 40px) clamp(12px, 2.5vw, 15px);
  }
}

@media (max-width: 480px) {
  .video-carousel-section {
    padding: clamp(25px, 4.5vw, 35px) clamp(10px, 2vw, 12px);
  }

  .carousel-container {
    height: clamp(580px, 120vw, 620px);
  }

  .center-card .card-content {
    width: clamp(260px, 70vw, 280px);
    height: clamp(440px, 112vw, 460px);
    border-radius: clamp(26px, 6vw, 30px);
  }

  .center-card .video-thumbnail {
    height: 100%;
    border-radius: clamp(26px, 6vw, 30px);
  }

  .nav-button {
    width: clamp(36px, 8vw, 40px);
    height: clamp(36px, 8vw, 40px);
  }

  .nav-button svg {
    width: clamp(18px, 4vw, 20px);
    height: clamp(18px, 4vw, 20px);
  }

  .play-button {
    width: clamp(55px, 12vw, 60px);
    height: clamp(55px, 12vw, 60px);
  }
}

@media (max-width: 380px) {
  .video-carousel-section {
    padding: 20px 10px;
  }

  .carousel-container {
    height: 560px;
  }

  .center-card .card-content {
    width: 240px;
    height: 420px;
    border-radius: 24px;
  }

  .center-card .video-thumbnail {
    height: 100%;
    border-radius: 24px;
  }

  .nav-button {
    width: 34px;
    height: 34px;
  }

  .nav-button svg {
    width: 17px;
    height: 17px;
  }

  .play-button {
    width: 50px;
    height: 50px;
  }
}
</style>
